import dynamic from 'next/dynamic'

import {
  margin,
  mq,
  spacing,
  styled,
  Text,
} from '@thg-commerce/enterprise-theme'
import {
  Button,
  ProductImage,
  ProductPrice,
  Skeleton,
} from '@thg-commerce/gravity-elements'

const GravityChevronRight = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/ChevronRight'),
)

export const Title = styled.h3`
  text-align: ${(props) =>
    props.theme.widget.productRecommendations.frequentlyBought?.text?.align ||
    'initial'};
  ${(props) =>
    Text(
      props.theme.widget.productRecommendations?.frequentlyBought?.text?.font
        ?.entry || 'large1',
      props.theme.widget.productRecommendations?.frequentlyBought?.text?.font
        ?.style || 'alternate',
    )};
  margin: ${spacing(2)} 0;

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    margin: ${spacing(3)} 0;
  }
  ${(props) =>
    props.theme.widget.productRecommendations?.frequentlyBought?.text?.font
      ?.textColor &&
    `color: ${props.theme.widget.productRecommendations.frequentlyBought.text.font.textColor};`}

  ${(props) =>
    props.theme.widget.productRecommendations?.frequentlyBought?.text?.font
      ?.transform &&
    `text-transform: ${props.theme.widget.productRecommendations.frequentlyBought.text.font.transform};`}
`

export const Container = styled.div`
  margin-bottom: ${spacing(2)};
  border: solid 1px ${(props) => props.theme.colors.palette.greys.light};
  background-color: ${(props) => props.theme.colors.palette.greys.white};

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    display: flex;
  }
`

export const ProductRowContainer = styled.div`
  display: flex;
`

export const StyledProductOptionsRendererDiv = styled.div<{
  enableFBTAlternateStyles?: boolean
}>`
  max-width: 342px;
  ${(props) =>
    margin(
      props.theme.widget?.productRecommendations.fbtProductOption.margin
        ?.smallDevice,
    )};
  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    ${(props) =>
      margin(
        props.theme.widget.productRecommendations.fbtProductOption.margin
          .mediumDevice,
      )};
  }
  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    ${(props) =>
      margin(
        props.enableFBTAlternateStyles
          ? props.theme.widget.productRecommendations.fbtProductOption.margin
              .alternateLargeDevice
          : props.theme.widget.productRecommendations.fbtProductOption.margin
              .largeDevice,
      )};
  }
`

export const SubTitle = styled.h4`
  ${(props) =>
    Text(
      props.theme.widget.productRecommendations?.frequentlyBought?.subtitle
        ?.font?.entry || 'bodyText',
      'default',
    )};
`

export const ProductText = styled.h5<{ productTwoText?: boolean }>`
  ${(props) =>
    Text(
      props.theme.widget.productRecommendations?.frequentlyBought?.product
        ?.description?.font?.entry || 'medium1',
      'alternate',
    )};
  text-decoration: ${(props) => (props.productTwoText ? 'underline' : 'none')};
`

export const ProductOneContainer = styled.div<{
  enableFBTAlternateStyles?: boolean
}>`
  padding: ${spacing(3)} ${spacing(3)} 0 ${spacing(3)};

  ${({ theme }) => mq(theme.breakpointUtils.map, 'md')} {
    padding: ${(props) =>
      props.enableFBTAlternateStyles
        ? `${spacing(2)} ${spacing(3)} 0 ${spacing(3)}`
        : `${spacing(4)} ${spacing(4)} 0 ${spacing(4)}`};
  }
`

export const ProductTwoContainer = styled.div<{
  enableFBTAlternateStyles?: boolean
}>`
  padding: 0 ${spacing(1)} ${spacing(3)} ${spacing(3)};
  background-color: ${(props) => props.theme.colors.palette.greys.lighter};

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    padding: ${(props) =>
      props.enableFBTAlternateStyles
        ? `0 ${spacing(2)} ${spacing(1)} ${spacing(4)}`
        : `0 ${spacing(2)} ${spacing(4)} ${spacing(4)}`};
  }
`

export const ProductContainerDetailsContent = styled.div`
  width: 90%;
`

export const ProductsContainer = styled.div`
  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    width: 60%;
    border-right: solid 1px ${(props) => props.theme.colors.palette.greys.light};
  }
`

export const StyledImage = styled(ProductImage)`
  margin-right: ${spacing(2)};
  flex-shrink: 0;
`

export const StyledProductPrice = styled(ProductPrice)`
  * {
    ${(props) =>
      Text(
        props.theme.widget.productRecommendations?.frequentlyBought?.product
          ?.price?.font?.entry || 'medium1',
        'alternate',
      )};
  }
`

export const SummaryPrice = styled(ProductPrice)`
  * {
    ${Text('medium1', 'alternate')};
  }
  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    align-items: center;
  }
`

export const SummaryContainer = styled.div`
  padding: ${spacing(3)};
  border-top: solid 1px ${(props) => props.theme.colors.palette.greys.light};

  ${SummaryPrice} * {
    ${(props) =>
      Text(
        props.theme.widget.productRecommendations?.frequentlyBought?.price?.text
          ?.font?.entry || 'large1',
        'alternate',
      )};
  }

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    align-items: center;
    text-align: ${(props) =>
      props.theme.widget.productRecommendations?.frequentlyBought?.price?.cta
        ?.textAlign || 'left'};
    margin: auto;
    border-top: none;
    ${SummaryPrice} {
      justify-content: center;
      margin-bottom: ${spacing(2)};
    }
  }
`

export const StyledButton = styled(Button)`
  margin-top: ${spacing(2)};
  text-align: center;

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    margin-top: ${spacing(2)};
  }
`

export const PlusContainer = styled.div<{ enableFBTAlternateStyles?: boolean }>`
  padding: ${spacing(3)} 0;
  background: linear-gradient(
    to bottom,
    ${(props) => props.theme.colors.palette.greys.white} 50%,
    ${(props) => props.theme.colors.palette.greys.lighter} 50%
  );

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    padding: ${(props) =>
      props.enableFBTAlternateStyles ? `${spacing(1)} 0 ` : `${spacing(3)} 0`};
  }
`

export const PlusWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  left: ${spacing(6)};
  height: ${spacing(4)};
  width: ${spacing(4)};
  text-align: center;
  border-radius: 50%;
  background-color: ${(props) => props.theme.colors.palette.greys.white};

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    left: ${spacing(7)};
  }
`

export const StyledChevronRight = styled(GravityChevronRight)`
  margin-left: auto;
`

export const StyledLink = styled.a`
  display: flex;
  flex-grow: 1;
  align-items: center;
  text-decoration: none;

  &:hover {
    ${ProductText} {
      color: ${(props) => props.theme.colors.palette.greys.dark};
      text-decoration: none;
    }
    ${StyledProductPrice},${SubTitle} {
      color: #000000;
    }
  }

  &:focus {
    border: solid 1px ${(props) => props.theme.colors.palette.greys.dark};
    ${ProductText} {
      color: ${(props) => props.theme.colors.palette.greys.dark};
      text-decoration: none;
    }
    ${StyledProductPrice},${SubTitle} {
      color: #000000;
    }
  }

  &:focus-visible {
    outline: solid 3px ${(props) => props.theme.colors.palette.brand.base};
  }

  &:active {
    border: none;
    ${ProductText} {
      color: ${(props) => props.theme.colors.palette.greys.darker};
      text-decoration: none;
    }
  }
`

export const TextSkeleton = styled(Skeleton)<{
  width: string
  bottomSpacing?: boolean
}>`
  margin-bottom: ${(props) => (props.bottomSpacing ? spacing(1) : '')};
  width: ${(props) => props.width || ''};
  height: 18px;
`

export const ImageSkeleton = styled(Skeleton)`
  height: 80px;
  width: 80px;
  margin-right: ${spacing(2)};
`

export const ButtonSkeleton = styled(Skeleton)`
  height: 48px;
  width: 180px;
  margin-top: ${spacing(2)};

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    margin-top: ${spacing(4)};
  }
`

export const GravityPlusSkeleton = styled(Skeleton)`
  position: relative;
  bottom: ${spacing(-2)};
  left: ${spacing(5)};
  height: 32px;
  width: 32px;
  margin-top: ${spacing(-4)};
  border-radius: 50%;
`
